/* THE SUMMIT DIGITAL VENTURE — product stage + inspector (Phase 1)
   In-place product selection: Section Rendering API fetch, View Transitions zoom
   from the selected piece (fade fallback), history integration, focus management.
   Inspector: zoom / pan (wheel, pinch, double-tap, keyboard, buttons) and an
   optional 360° image-sequence rotation with light inertia.
   Future WebGL / model-viewer phases can hook on [data-tsdv-stage] data attributes
   and the 'tsdv:stage-open' / 'tsdv:stage-close' events. */
(() => {
  'use strict';

  const config = window.tsdv || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const html = document.documentElement;
  // constants.js declares PUB_SUB_EVENTS with top-level const (not on window).
  // eslint-disable-next-line no-undef
  const EVENTS = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;

  /* ============================================================ Inspector */
  const MAX_ZOOM = 4;

  class TsdvInspector extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.viewport = this.querySelector('[data-tsdv-viewport]');
      this.slides = Array.from(this.querySelectorAll('.tsdv-inspect__slide'));
      this.thumbs = Array.from(this.querySelectorAll('[data-tsdv-thumb]'));
      this.position = this.querySelector('[data-tsdv-media-pos]');
      this.spinEl = this.querySelector('[data-tsdv-spin]');
      this.spinButton = this.querySelector('[data-tsdv-spin-toggle]');
      this.frames = (this.dataset.spin || '').split('|').filter(Boolean);
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.zoom = { s: 1, x: 0, y: 0 };
      this.pointers = new Map();
      this.lastTap = { t: 0, x: 0, y: 0 };

      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.showById(thumb.dataset.tsdvThumb))
      );
      this.querySelectorAll('[data-tsdv-zoom]').forEach((button) =>
        button.addEventListener('click', () => {
          this.classList.add('has-interacted');
          const action = button.dataset.tsdvZoom;
          if (action === 'in') this.zoomBy(1.5, null, true);
          else if (action === 'out') this.zoomBy(1 / 1.5, null, true);
          else this.reset(true);
        })
      );
      if (this.spinButton) this.spinButton.addEventListener('click', () => this.toggleSpin());

      if (this.viewport) {
        this.viewport.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
        this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event));
        this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event, true));
        this.viewport.addEventListener('keydown', (event) => this.onKey(event));
        this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
      }

      if (typeof window.subscribe === 'function' && EVENTS) {
        this.unsubscribe = window.subscribe(EVENTS.variantChange, ({ data }) => {
          if (!data || data.sectionId !== this.dataset.section) return;
          const media = data.variant && data.variant.featured_media;
          if (media) this.showById(media.id);
        });
      }
      this.sync();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      cancelAnimationFrame(this.spinFrame);
    }

    get image() {
      const slide = this.slides[this.index];
      return slide ? slide.querySelector('img[data-tsdv-zoomable]') : null;
    }

    show(index) {
      if (!this.slides.length) return;
      const next = (index + this.slides.length) % this.slides.length;
      if (next === this.index) return;
      this.reset(false);
      this.slides.forEach((slide, i) => {
        const active = i === next;
        slide.classList.toggle('is-active', active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        if (active) {
          const img = slide.querySelector('img');
          if (img) img.loading = 'eager';
        }
      });
      this.index = next;
      this.sync();
    }

    showById(id) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
      if (index > -1) this.show(index);
    }

    sync() {
      const id = this.slides[this.index] && this.slides[this.index].dataset.mediaId;
      this.thumbs.forEach((thumb) => thumb.setAttribute('aria-current', String(thumb.dataset.tsdvThumb === id)));
      if (this.position) this.position.textContent = String(this.index + 1).padStart(2, '0');
    }

    /* ---- zoom / pan */
    metrics() {
      const img = this.image;
      if (!img) return null;
      const rect = img.getBoundingClientRect();
      const s = this.zoom.s;
      return {
        view: this.viewport.getBoundingClientRect(),
        left: rect.left - this.zoom.x,
        top: rect.top - this.zoom.y,
        width: rect.width / s,
        height: rect.height / s,
        rect,
      };
    }

    clamp(s, x, y, m) {
      if (s <= 1.001) return { s: 1, x: 0, y: 0 };
      const w = m.width * s;
      const h = m.height * s;
      const v = m.view;
      const cx = w <= v.width ? v.left + v.width / 2 - m.left - w / 2 : Math.min(v.left - m.left, Math.max(v.right - m.left - w, x));
      const cy = h <= v.height ? v.top + v.height / 2 - m.top - h / 2 : Math.min(v.top - m.top, Math.max(v.bottom - m.top - h, y));
      return { s, x: cx, y: cy };
    }

    zoomTo(target, point, animate) {
      const m = this.metrics();
      if (!m) return;
      const s0 = this.zoom.s;
      const s1 = Math.min(MAX_ZOOM, Math.max(1, target));
      const c = point || { x: m.view.left + m.view.width / 2, y: m.view.top + m.view.height / 2 };
      const x = c.x - m.left - (s1 * (c.x - m.rect.left)) / s0;
      const y = c.y - m.top - (s1 * (c.y - m.rect.top)) / s0;
      this.zoom = this.clamp(s1, x, y, m);
      this.apply(animate);
    }

    zoomBy(factor, point, animate) {
      this.zoomTo(this.zoom.s * factor, point, animate);
    }

    panTo(x, y) {
      const m = this.metrics();
      if (!m) return;
      this.zoom = this.clamp(this.zoom.s, x, y, m);
      this.apply(false);
    }

    reset(animate) {
      this.zoom = { s: 1, x: 0, y: 0 };
      this.apply(animate);
    }

    apply(animate) {
      const img = this.image;
      if (!img) return;
      this.classList.toggle('is-animating', !!animate && !reduceMotion.matches);
      img.style.setProperty('--s', this.zoom.s.toFixed(4));
      img.style.setProperty('--tx', `${this.zoom.x.toFixed(2)}px`);
      img.style.setProperty('--ty', `${this.zoom.y.toFixed(2)}px`);
      this.classList.toggle('is-zoomed', this.zoom.s > 1.01);
      if (this.zoom.s > 1.2 && !img.dataset.hires) {
        img.dataset.hires = 'true';
        img.sizes = `${Math.round(Math.min(this.zoom.s, 3) * 100)}vw`;
      }
    }

    onWheel(event) {
      if (this.spinning || !this.image) return;
      if (this.zoom.s <= 1.01 && !event.ctrlKey) return;
      event.preventDefault();
      this.classList.add('has-interacted');
      const factor = Math.exp(-event.deltaY * (event.ctrlKey ? 0.012 : 0.0025));
      this.zoomBy(factor, { x: event.clientX, y: event.clientY }, false);
    }

    onPointerDown(event) {
      if (event.button !== 0 && event.pointerType === 'mouse') return;
      if (event.target.closest('button, a, deferred-media, product-model, model-viewer')) return;
      this.viewport.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 2) {
        const [a, b] = Array.from(this.pointers.values());
        this.pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, s: this.zoom.s };
        this.drag = null;
        return;
      }
      this.drag = {
        x: event.clientX,
        y: event.clientY,
        tx: this.zoom.x,
        ty: this.zoom.y,
        moved: 0,
        type: event.pointerType,
        frame: this.frame || 0,
        lastX: event.clientX,
        lastT: performance.now(),
        v: 0,
      };
      if (this.spinning) cancelAnimationFrame(this.spinFrame);
      if (this.zoom.s > 1.01) this.classList.add('is-panning');
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pinch && this.pointers.size === 2) {
        const [a, b] = Array.from(this.pointers.values());
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        this.classList.add('has-interacted');
        this.zoomTo((this.pinch.s * d) / this.pinch.d, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, false);
        return;
      }
      const drag = this.drag;
      if (!drag) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      drag.moved = Math.max(drag.moved, Math.hypot(dx, dy));
      if (this.spinning) {
        const now = performance.now();
        const perFrame = this.viewport.clientWidth / this.frames.length / 1.1;
        drag.v = 0.7 * ((event.clientX - drag.lastX) / perFrame / Math.max(1, now - drag.lastT)) + 0.3 * drag.v;
        drag.lastX = event.clientX;
        drag.lastT = now;
        this.setFrame(drag.frame - dx / perFrame);
        this.classList.add('has-interacted');
        return;
      }
      if (this.zoom.s > 1.01) this.panTo(drag.tx + dx, drag.ty + dy);
    }

    onPointerUp(event, cancelled) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      this.classList.remove('is-panning');
      if (this.pinch) {
        if (this.pointers.size < 2) this.pinch = null;
        this.drag = null;
        return;
      }
      const drag = this.drag;
      this.drag = null;
      if (!drag || cancelled) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;

      if (this.spinning) {
        if (!reduceMotion.matches && Math.abs(drag.v) > 0.004) this.glide(-drag.v);
        return;
      }
      if (this.zoom.s <= 1.01 && Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        this.show(this.index + (dx < 0 ? 1 : -1));
        return;
      }
      if (drag.moved > 8) return;
      const point = { x: event.clientX, y: event.clientY };
      if (drag.type === 'mouse') {
        this.classList.add('has-interacted');
        if (this.zoom.s > 1.01) this.reset(true);
        else this.zoomTo(2.5, point, true);
        return;
      }
      const now = performance.now();
      if (now - this.lastTap.t < 320 && Math.hypot(point.x - this.lastTap.x, point.y - this.lastTap.y) < 32) {
        this.classList.add('has-interacted');
        if (this.zoom.s > 1.01) this.reset(true);
        else this.zoomTo(2.5, point, true);
        this.lastTap.t = 0;
      } else {
        this.lastTap = { t: now, x: point.x, y: point.y };
      }
    }

    onKey(event) {
      const key = event.key;
      if (this.spinning && (key === 'ArrowLeft' || key === 'ArrowRight')) {
        event.preventDefault();
        this.setFrame((this.frame || 0) + (key === 'ArrowRight' ? 1 : -1));
        return;
      }
      if (key === '+' || key === '=') {
        event.preventDefault();
        this.zoomBy(1.5, null, true);
      } else if (key === '-' || key === '_') {
        event.preventDefault();
        this.zoomBy(1 / 1.5, null, true);
      } else if (key === '0') {
        event.preventDefault();
        this.reset(true);
      } else if (key === 'Escape' && (this.zoom.s > 1.01 || this.spinning)) {
        event.preventDefault();
        event.stopPropagation();
        if (this.spinning) this.toggleSpin();
        this.reset(true);
      } else if (key.startsWith('Arrow')) {
        if (this.zoom.s > 1.01) {
          event.preventDefault();
          const step = 64;
          const d = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] }[key];
          this.panTo(this.zoom.x + d[0], this.zoom.y + d[1]);
        } else if (key === 'ArrowLeft' || key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + (key === 'ArrowRight' ? 1 : -1));
        }
      }
    }

    /* ---- 360° image sequence */
    toggleSpin() {
      if (this.frames.length < 8 || !this.spinEl) return;
      this.spinning = !this.spinning;
      this.reset(false);
      this.classList.toggle('is-spin', this.spinning);
      this.spinEl.hidden = !this.spinning;
      this.querySelector('.tsdv-inspect__slides').style.visibility = this.spinning ? 'hidden' : '';
      if (this.spinButton) this.spinButton.setAttribute('aria-pressed', String(this.spinning));
      if (this.spinning && !this.preloaded) this.preload();
      if (!this.spinning) cancelAnimationFrame(this.spinFrame);
    }

    preload() {
      this.preloaded = true;
      const bar = this.spinEl.querySelector('.tsdv-inspect__spin-progress');
      let loaded = 0;
      this.frameImages = this.frames.map((src) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = img.onerror = () => {
          loaded += 1;
          if (bar) bar.firstElementChild.style.setProperty('--p', (loaded / this.frames.length).toFixed(3));
          if (bar && loaded === this.frames.length) bar.hidden = true;
        };
        img.src = src;
        return img;
      });
      this.setFrame(0);
    }

    setFrame(value) {
      const n = this.frames.length;
      this.frame = value;
      const index = ((Math.round(value) % n) + n) % n;
      const img = this.spinEl.querySelector('img');
      if (img && this.currentFrame !== index) {
        img.src = this.frames[index];
        this.currentFrame = index;
      }
    }

    glide(velocity) {
      let v = velocity * 16;
      const tick = () => {
        this.setFrame(this.frame + v);
        v *= 0.93;
        if (Math.abs(v) > 0.02) this.spinFrame = requestAnimationFrame(tick);
      };
      this.spinFrame = requestAnimationFrame(tick);
    }
  }
  if (!customElements.get('tsdv-inspector')) customElements.define('tsdv-inspector', TsdvInspector);

  /* ======================================================== Product stage */
  const root = document.querySelector('[data-tsdv-stage-root]');
  const body = root && root.querySelector('[data-tsdv-stage-body]');

  // Page mode: "back" returns to the previous scene when it was ours.
  document.addEventListener('click', (event) => {
    const back = event.target.closest('[data-tsdv-history-back]');
    if (!back) return;
    try {
      if (document.referrer && new URL(document.referrer).origin === location.origin && history.length > 1) {
        event.preventDefault();
        history.back();
      }
    } catch (e) {
      /* follow the link */
    }
  });

  if (!root || !body) return;

  const TRIGGER = 'a[data-tsdv-stage-trigger], main .card-wrapper a[href*="/products/"]';
  const cache = new Map();
  const state = { open: false, busy: false, trigger: null, media: null, list: [], index: -1, pushed: false, title: '', inert: [] };

  const canTransition = () => config.viewTransitions !== false && 'startViewTransition' in document && !reduceMotion.matches;

  const sectionUrl = (href) => {
    const url = new URL(href, location.href);
    url.searchParams.set('section_id', config.stageSection || 'tsdv-product-stage');
    return url.pathname + url.search;
  };

  const productPath = (href) => {
    const url = new URL(href, location.href);
    const variant = url.searchParams.get('variant');
    return url.pathname + (variant ? `?variant=${variant}` : '');
  };

  const eligible = (link, event) => {
    if (!config.stageEnabled || !link || !link.href) return false;
    if (event && (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.closest('[data-tsdv-stage]')) return false;
    const url = new URL(link.href, location.href);
    return url.origin === location.origin && /\/products\/[^/]+/.test(url.pathname);
  };

  const fetchStage = (href) => {
    const key = sectionUrl(href);
    if (!cache.has(key)) {
      const request = fetch(key, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .catch((error) => {
          cache.delete(key);
          throw error;
        });
      cache.set(key, request);
    }
    return cache.get(key).then((text) => {
      const node = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-tsdv-stage]');
      if (!node) throw new Error('Stage markup missing');
      return document.importNode(node, true);
    });
  };

  const heroOf = (stage) => stage && stage.querySelector('.tsdv-inspect__slide.is-active img');

  const decodeHero = async (stage) => {
    const img = heroOf(stage);
    if (!img) return;
    img.loading = 'eager';
    const probe = new Image();
    probe.sizes = img.sizes;
    probe.srcset = img.srcset;
    probe.src = img.src;
    try {
      await Promise.race([probe.decode(), new Promise((resolve) => setTimeout(resolve, 650))]);
    } catch (e) {
      /* decode failures are not fatal */
    }
  };

  const inViewport = (el) => {
    if (!el || !el.isConnected) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.bottom > 0 && r.right > 0 && r.top < innerHeight && r.left < innerWidth;
  };

  const listFor = (link) => {
    const scope = link.closest('[data-tsdv-rail-track], [data-tsdv-wishlist-grid], .product-grid, ul');
    if (!scope) return [link];
    const seen = new Set();
    return Array.from(scope.querySelectorAll(TRIGGER)).filter((a) => {
      const path = new URL(a.href, location.href).pathname;
      if (seen.has(path) || !eligible(a)) return false;
      seen.add(path);
      return true;
    });
  };

  const mediaFor = (link) => {
    const piece = link.closest('[data-tsdv-piece], .card-wrapper');
    return piece ? piece.querySelector('[data-tsdv-stage-media] img, .card__media img') : null;
  };

  const lockPage = () => {
    html.classList.add('tsdv-stage-open');
    state.inert = Array.from(document.body.children).filter(
      (el) => el !== root && !el.matches('script, style, link, template, cart-drawer, [data-tsdv-toast]') && !el.inert
    );
    state.inert.forEach((el) => (el.inert = true));
  };

  const unlockPage = () => {
    html.classList.remove('tsdv-stage-open');
    state.inert.forEach((el) => (el.inert = false));
    state.inert = [];
  };

  const ensureModelSupport = (stage) => {
    if (!stage.querySelector('product-model') || customElements.get('product-model') || document.getElementById('TsdvModelScript')) return;
    const assets = config.assets || {};
    [assets.modelViewerCss, assets.modelViewerUiCss].forEach((href) => {
      if (!href) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    });
    const script = document.createElement('script');
    script.id = 'TsdvModelScript';
    script.src = assets.productModel;
    script.defer = true;
    document.head.appendChild(script);
  };

  const updateNav = (stage) => {
    const nav = stage.querySelector('[data-tsdv-stage-nav]');
    if (!nav) return;
    const total = state.list.length;
    nav.hidden = total < 2 || state.index < 0;
    if (nav.hidden) return;
    nav.querySelector('[data-tsdv-stage-pos]').textContent = String(state.index + 1).padStart(2, '0');
    nav.querySelector('[data-tsdv-stage-total]').textContent = String(total).padStart(2, '0');
    nav.querySelector('[data-tsdv-stage-prev]').disabled = state.index <= 0;
    nav.querySelector('[data-tsdv-stage-next]').disabled = state.index >= total - 1;
  };

  const bindStage = (stage) => {
    stage.querySelectorAll('[data-tsdv-stage-close]').forEach((button) => button.addEventListener('click', () => close()));
    const prev = stage.querySelector('[data-tsdv-stage-prev]');
    const next = stage.querySelector('[data-tsdv-stage-next]');
    if (prev) prev.addEventListener('click', () => step(-1));
    if (next) next.addEventListener('click', () => step(1));
    ensureModelSupport(stage);
    updateNav(stage);
    if (window.TsdvUI) {
      window.TsdvUI.syncWishlist();
      window.TsdvUI.syncCartCounts();
    }
  };

  const setTitle = (stage) => {
    if (!state.title) state.title = document.title;
    const name = stage.dataset.productTitle || '';
    document.title = config.brand ? `${name} – ${config.brand}` : name;
  };

  const focusStage = (stage) => {
    const title = stage.querySelector('.tsdv-stage__title');
    if (title) title.focus({ preventScroll: true });
  };

  async function open(link, options = {}) {
    if (state.busy) return;
    state.busy = true;
    const piece = link.closest('[data-tsdv-piece], .card-wrapper');
    if (piece) piece.classList.add('is-loading');
    let stage;
    try {
      stage = await fetchStage(link.href);
      await decodeHero(stage);
    } catch (error) {
      state.busy = false;
      if (piece) piece.classList.remove('is-loading');
      window.location.href = link.href;
      return;
    }
    if (piece) piece.classList.remove('is-loading');

    state.trigger = link;
    state.media = mediaFor(link);
    state.list = options.list || listFor(link);
    const path = new URL(link.href, location.href).pathname;
    state.index = state.list.findIndex((a) => new URL(a.href, location.href).pathname === path);

    const mount = () => {
      body.replaceChildren(stage);
      root.hidden = false;
      lockPage();
      bindStage(stage);
    };

    if (canTransition() && inViewport(state.media) && heroOf(stage)) {
      root.classList.add('is-instant');
      state.media.style.viewTransitionName = 'tsdv-focus';
      const transition = document.startViewTransition(() => {
        state.media.style.viewTransitionName = '';
        mount();
        root.classList.add('is-open');
        heroOf(stage).style.viewTransitionName = 'tsdv-focus';
      });
      try {
        await transition.finished;
      } catch (e) {
        /* skipped transition */
      }
      const hero = heroOf(stage);
      if (hero) hero.style.viewTransitionName = '';
      root.classList.remove('is-instant');
    } else {
      mount();
      root.getBoundingClientRect();
      requestAnimationFrame(() => root.classList.add('is-open'));
    }

    if (options.push !== false) history.pushState({ tsdvStage: true }, '', productPath(link.href));
    state.pushed = true;
    setTitle(stage);
    state.open = true;
    state.busy = false;
    focusStage(stage);
    document.dispatchEvent(new CustomEvent('tsdv:stage-open', { detail: { stage, handle: stage.dataset.productHandle } }));
  }

  async function step(delta) {
    const target = state.list[state.index + delta];
    if (!target || state.busy) return;
    state.busy = true;
    let stage;
    try {
      stage = await fetchStage(target.href);
      await decodeHero(stage);
    } catch (error) {
      state.busy = false;
      return;
    }
    state.index += delta;
    state.trigger = target;
    state.media = mediaFor(target);
    const swap = () => {
      body.replaceChildren(stage);
      bindStage(stage);
    };
    if (canTransition()) {
      const transition = document.startViewTransition(swap);
      try {
        await transition.finished;
      } catch (e) {
        /* skipped */
      }
    } else {
      swap();
      if (!reduceMotion.matches && body.animate) {
        body.animate(
          [
            { opacity: 0, transform: `translateX(${delta * 16}px)` },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 360, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
        );
      }
    }
    // Keep the scene behind in sync so closing returns to the right piece.
    const track = target.closest('[data-tsdv-rail-track]');
    const item = target.closest('li');
    if (track && item && getComputedStyle(track).overflowX !== 'visible') {
      track.scrollTo({ left: item.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || 0), behavior: 'auto' });
    }
    history.replaceState({ tsdvStage: true }, '', productPath(target.href));
    setTitle(stage);
    state.busy = false;
    focusStage(stage);
  }

  function close() {
    if (!state.open || state.busy) return;
    if (state.pushed && history.state && history.state.tsdvStage) {
      history.back();
      return;
    }
    teardown();
  }

  async function teardown() {
    if (!state.open) return;
    state.open = false;
    state.pushed = false;
    const stage = body.firstElementChild;
    const hero = heroOf(stage);
    const media = state.media;
    const trigger = state.trigger;

    const finish = () => {
      root.hidden = true;
      root.classList.remove('is-open');
      body.replaceChildren();
      unlockPage();
      if (state.title) document.title = state.title;
      state.title = '';
      if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('tsdv:stage-close'));
    };

    if (canTransition() && hero && inViewport(media)) {
      root.classList.add('is-instant');
      hero.style.viewTransitionName = 'tsdv-focus';
      const transition = document.startViewTransition(() => {
        hero.style.viewTransitionName = '';
        finish();
        media.style.viewTransitionName = 'tsdv-focus';
      });
      try {
        await transition.finished;
      } catch (e) {
        /* skipped */
      }
      media.style.viewTransitionName = '';
      root.classList.remove('is-instant');
    } else {
      root.classList.remove('is-open');
      setTimeout(finish, reduceMotion.matches ? 0 : 360);
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest(TRIGGER);
    if (!eligible(link, event)) return;
    event.preventDefault();
    open(link);
  });

  const prefetch = (event) => {
    const link = event.target.closest && event.target.closest(TRIGGER);
    if (!link || link.dataset.tsdvPrefetched || !eligible(link)) return;
    link.dataset.tsdvPrefetched = 'true';
    fetchStage(link.href).catch(() => {});
  };
  document.addEventListener('pointerover', prefetch, { passive: true });
  document.addEventListener('focusin', prefetch);
  document.addEventListener('touchstart', prefetch, { passive: true });

  root.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    const drawer = document.querySelector('cart-drawer.active');
    if (drawer) return;
    event.preventDefault();
    close();
  });

  window.addEventListener('popstate', (event) => {
    if (state.open) {
      teardown();
    } else if (event.state && event.state.tsdvStage) {
      const link = document.createElement('a');
      link.href = location.href;
      open(link, { push: false, list: [link] });
    }
  });

  // Dawn's product-info replaces history state on variant change; keep ours.
  if (typeof window.subscribe === 'function' && EVENTS) {
    window.subscribe(EVENTS.variantChange, () => {
      if (state.open) history.replaceState({ tsdvStage: true }, '', location.pathname + location.search);
    });
  }

  window.TsdvStage = { open: (href) => {
    const link = document.createElement('a');
    link.href = href;
    return open(link, { list: [link] });
  }, close };
})();
